import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Printer, FileText, Briefcase, AlertTriangle, ShieldCheck, CheckSquare, Trash2, Info, RefreshCw, Download, ChevronDown } from 'lucide-react';
import Button from '../../common/Button';
import { useTranslation } from 'react-i18next';
import { useAnalysis } from '../../../context/AnalysisContext';
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier1Store from '../../../stores/useAtelier1Store';
import { DREADED_EVENT_IMPACTS } from '../../../constants';
import { getTranslatedDreadedEventImpacts, cleanImpactValue } from '../../../utils/translatedConstants';
import RiskTreatmentGuideModal from './RiskTreatmentGuideModal';
import { showSuccessToast, showErrorToast, showLoadingToast } from '../../../utils/toastUtils';
import { normalizeRuleStatus, getRuleStatusConfig } from '../../../utils/ruleStatusNormalizer';
import { useSilentSaveListener } from '../../../hooks/useSilentSaveListener';



// --- Helper Function to get Severity Info (Copied from SecurityControls) ---
const getSeverityLevelInfo = (severity) => {
    const lowerSeverity = severity?.toLowerCase() || '';
    switch (lowerSeverity) {
        case 'catastrophic':
            return { level: 'G5', label: 'Catastrophique', textClass: 'text-white', bgClass: 'bg-black', value: 5 };
        case 'critical':
            return { level: 'G4', label: 'Critique', textClass: 'text-white', bgClass: 'bg-red-600', value: 4 };
        case 'high':
        case 'major':
            return { level: 'G3', label: 'Majeure', textClass: 'text-black', bgClass: 'bg-yellow-500', value: 3 };
        case 'moderate':
        case 'significative':
            return { level: 'G2', label: 'Significative', textClass: 'text-black', bgClass: 'bg-yellow-200', value: 2 };
        case 'minor':
        case 'mineure':
            return { level: 'G1', label: 'Mineure', textClass: 'text-black', bgClass: 'bg-cyan-300', value: 1 };
        default:
            // Don't warn here, expected for unselected values
            return { level: 'N/A', label: 'Non défini', textClass: 'text-gray-800', bgClass: 'bg-gray-200', value: 0 };
    }
};

// --- Helper to clean legacy/imported impact labels (e.g. "l'Impacts sur les coûts de développement" with stray "!" and letter-spacing) ---

// --- UPDATED: Helper Function for Treatment Option Labels ---
const getTreatmentOptionLabel = (optionValue) => {
    switch (optionValue) {
        case 'reduce': return 'Réduire';
        case 'transfer': return 'Transférer';
        case 'avoid': return 'Éviter';
        case 'accept': return 'Accepter';
        default: return '-';
    }
};

// --- UPDATED: Helper Function for Decision Labels (including rationale) ---
const getDecisionLabel = (decisionData) => {
    if (!decisionData || !decisionData.decision) return '-';
    switch (decisionData.decision) {
        case 'planned': return 'À planifier';
        case 'apply_before': return `Appliquer avant le ${decisionData.date || '[Date?]'}`;
        case 'abandoned': return 'Abandonner';
        // NEW: Add rationale for acceptance
        case 'accepted':
            const rationale = decisionData.acceptanceRationale ? ` (Raison: ${decisionData.acceptanceRationale})` : '';
            return `Accepter ${rationale}`;
        default: return '-';
    }
};

// --- Main Synthesis Component ---
const RiskTreatment = () => {
    const atelier1Store = useAtelier1Store();
    const analysisStore = useAnalysisStore();
    const { selectAnalysis } = useAnalysis();

    const currentAnalysis = analysisStore.currentAnalysis;
    const currentContextData = atelier1Store.contextData;
    const currentBusinessValues = atelier1Store.businessValues;
    const currentDreadedEvents = atelier1Store.dreadedEvents;
    const currentSecurityFramework = atelier1Store.securityFramework;
    const currentAnalysisControlPlan = atelier1Store.controlPlan;
    const allControls = atelier1Store.allControls;

    // Use i18next translation hook
    const { t } = useTranslation();

    // --- States for checkbox selection and deletion ---
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [showDeleteButton, setShowDeleteButton] = useState(false);
    const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);

    // --- NEW: Debugging useEffect ---
    useEffect(() => {
        if (!analysisStore.isWorkshopDataLoading) { // Only log after loading attempt
            console.log('[RiskTreatment] currentAnalysisControlPlan:', currentAnalysisControlPlan);
            console.log('[RiskTreatment] currentAnalysisControlPlan?.planData:', currentAnalysisControlPlan?.planData);
        }
    }, [currentAnalysisControlPlan, analysisStore.isWorkshopDataLoading]);

    // Update delete button visibility when selection changes
    useEffect(() => {
        setShowDeleteButton(selectedRows.size > 0);
    }, [selectedRows]);

    // Silent save: risk-treatment has no mutable data to save, resolve immediately
    useSilentSaveListener('risk-treatment', () => 'skipped');

    // --- Derived Data for the Report ---
    const analysisOverview = useMemo(() => ({
        name: currentAnalysis?.name || 'Analyse sans nom',
        date: currentAnalysis?.createdAt ? new Date(currentAnalysis.createdAt).toLocaleDateString() : 'Date inconnue',
        orgName: currentContextData?.organizationName || 'N/A',
        scope: currentContextData?.scope || 'N/A',
        missions: currentContextData?.missions || [],
        contraintes: currentContextData?.contraintes || [],
        hypotheses: currentContextData?.hypotheses || []
    }), [currentAnalysis, currentContextData]);

    const participantsList = useMemo(() => currentContextData?.participants || [], [currentContextData]);

    // --- UPDATED: Detailed Business Values and Assets ---
    const detailedBusinessValues = useMemo(() => {
        return (currentBusinessValues || []).map(bv => ({
            id: bv.id,
            name: bv.name,
            description: bv.description,
            nature: bv.nature || '',
            proprietaire: bv.proprietaire || { nom: '', type: '' },
            securityPillars: bv.securityPillars || [],
            assets: (bv.supportAssets || []).map(sa => ({
                id: sa.id,
                shortId: sa.shortId,
                name: sa.name,
                type: sa.type,
                description: sa.description,
                owner: sa.owner,
                location: sa.location,
                vendor: sa.vendor,
                product: sa.product,
                version: sa.version
            })) || [],
        }));
    }, [currentBusinessValues]);

    // --- UPDATED: Detailed Dreaded Events (adding impact field) ---
    const detailedDreadedEvents = useMemo(() => {
        return (currentDreadedEvents || []).map(de => {
            // ---> Find linked Business Value <--- (Keep for name)
            const linkedBv = (currentBusinessValues || []).find(bv => String(bv.id) === String(de.businessValue));
            const linkedBvName = linkedBv?.name || 'N/A';

            // Get impact labels and justifications if available
            const impactArray = de.impacts || (de.impact ? [de.impact] : []);
            const impactLabels = impactArray.length > 0 ?
                impactArray.map(impactValue => {
                    // Handle both old format (string) and new format (object with impact and justification)
                    if (typeof impactValue === 'string') {
                        const translatedImpacts = getTranslatedDreadedEventImpacts();
                        const impact = translatedImpacts.find(imp => imp.value === impactValue);
                        return impact ? impact.label : cleanImpactValue(impactValue);
                    } else if (impactValue && impactValue.impact) {
                        const translatedImpacts = getTranslatedDreadedEventImpacts();
                        const impact = translatedImpacts.find(imp => imp.value === impactValue.impact);
                        return impact ? impact.label : cleanImpactValue(impactValue.impact);
                    }
                    return 'Non spécifié';
                }) :
                ['Non spécifié'];

            // Extract justifications
            const impactJustifications = impactArray.length > 0 ?
                impactArray.map(impactValue => {
                    if (typeof impactValue === 'object' && impactValue.justification) {
                        // ponytail: strip copy-paste corruption (letter-spacing, "!'") from legacy/imported data without touching the DB
                        return cleanImpactValue(impactValue.justification);
                    }
                    return '';
                }).filter(Boolean) :
                [];

            return {
                id: de.id,
                shortId: de.shortId || '',
                name: de.name || 'Événement sans nom',
                description: de.description || 'Pas de description',
                severityInfo: getSeverityLevelInfo(de.severity),
                securityPillar: de.securityPillar || '',
                businessValueName: linkedBvName, // Keep BV Name
                businessValue: de.businessValue,
                isNonIntentional: de.businessValue === '__global__',
                impacts: impactArray, // Add impact values array
                impactLabels: impactLabels, // Add formatted impact labels
                impactJustifications: impactJustifications // Add impact justifications
            };
        });
    }, [currentDreadedEvents, currentBusinessValues]); // Dependency remains

    // --- UPDATED: Detailed Security Rules (adding linked DEs) ---
    const detailedSecurityRules = useMemo(() => {
        const rules = Object.values(currentSecurityFramework?.selectedRules || {}).flat();
        const ruleLinks = currentSecurityFramework?.ruleDreadedEvents || {}; // { ruleId: [deId1, deId2], ... }
        const allEventsMap = new Map((currentDreadedEvents || []).map(de => [String(de.id), de.name])); // Map de.id (string) to de.name
        const ruleGapComments = currentSecurityFramework?.ruleGapComments || {};

        return rules.map(rule => {
            const ruleId = String(rule.id || rule.rule_id);
            const linkedDeIds = (ruleLinks[ruleId] || []).filter(deId => deId !== '__v' && deId !== '_id');
            const linkedDeNames = linkedDeIds
                .map(deId => allEventsMap.get(String(deId)) || `ID:${deId}?`)
                .filter(Boolean); // Get names, fallback to ID, filter out nulls if any

            // Normalize status to numeric 0-4
            const normalizedStatus = normalizeRuleStatus(rule.status);
            const statusConfig = getRuleStatusConfig(normalizedStatus, t);

            return {
                id: ruleId || `rule_${Math.random()}`,
                name: rule.name || rule.title || 'Règle sans nom',
                description: rule.description || 'Pas de description',
                status: normalizedStatus, // Store normalized numeric status
                statusLabel: statusConfig.label, // Use config label
                linkedDreadedEvents: linkedDeNames,
                gapComment: ruleGapComments[ruleId] || '',
            };
        });
    }, [currentSecurityFramework, currentDreadedEvents]);

    // --- Existing Detailed Treatment Plan (ensure control details are rendered) ---
    const detailedTreatmentPlan = useMemo(() => {
        // Access the nested 'plan' object
        const actualPlanObject = currentAnalysisControlPlan?.planData?.plan || {};
        const allRules = Object.values(currentSecurityFramework?.selectedRules || {}).flat();
        // ---> CREATE Map for efficient control lookup <---
        const allControlsMap = new Map((allControls || []).map(c => [String(c.id), c]));
        const ruleGapComments = currentSecurityFramework?.ruleGapComments || {};


        const processedPlan = Object.values(actualPlanObject).map((entry, index) => {
            // Find the corresponding rule by ID
            const rule = allRules.find(r => r.id === entry.ruleId || r.rule_id === entry.ruleId);
            const ruleName = rule?.name || rule?.title || 'Règle inconnue';

            // Find the corresponding dreaded event by ID
            const dreadedEvent = (currentDreadedEvents || []).find(de => de.id === entry.dreadedEventId);
            const deName = dreadedEvent?.name || 'Événement inconnu';
            // Get initial severity from the found dreaded event
            const initialSeverity = getSeverityLevelInfo(dreadedEvent?.severity);

            // Get impact information (support both old impact field and new impacts array)
            const impactArray = dreadedEvent?.impacts || (dreadedEvent?.impact ? [dreadedEvent.impact] : []);
            const impactLabels = impactArray.length > 0 ?
                impactArray.map(impactValue => {
                    const rawImpact = (typeof impactValue === 'object' && impactValue !== null) ? impactValue.impact : impactValue;
                    const translatedImpacts = getTranslatedDreadedEventImpacts();
                    const impact = translatedImpacts.find(imp => imp.value === rawImpact);
                    return impact ? impact.label : cleanImpactValue(rawImpact);
                }) :
                ['Non spécifié'];

            return {
                id: `${entry.ruleId}_${entry.dreadedEventId}_${index}`, // Create a unique key for mapping
                // ---> ADD Original IDs and Value <---
                ruleId: entry.ruleId,
                dreadedEventId: entry.dreadedEventId,
                treatmentOptionValue: entry.treatmentOption, // Keep the raw value
                // ---> Existing display properties <---
                ruleName: ruleName,
                deName: deName,
                initialSeverity: initialSeverity,
                impacts: impactArray, // Add impact values array
                impactLabels: impactLabels, // Add formatted impact labels
                treatmentOption: getTreatmentOptionLabel(entry.treatmentOption), // Keep label for display table
                controls: (entry.controls || []).map(ctrl => { // Ensure we map control details
                    // ---> LOOKUP Control Name <---
                    const foundControl = allControlsMap.get(String(ctrl.controlId));
                    const controlName = foundControl?.name || ctrl.name || 'Contrôle inconnu'; // Use found name, fallback to saved name, then unknown
                    const controlDescription = foundControl?.description || 'Pas de description'; // Use description from master list if available
                    // console.log(`[RiskTreatment] Control Lookup: ID=${ctrl.controlId}, Found=${!!foundControl}, Name=${controlName}`); // Debug log
                    // ---> ADD DETAILED LOGGING <--- (Also update to check ctrl.name)
                    console.log(`[RiskTreatment] Processing Control:`, {
                        controlFromPlan: ctrl, // Log the raw object from planData
                        foundInMasterList: foundControl, // Log the result of map lookup
                        nameFromPlan: ctrl.name, // Explicitly log the name from plan (using .name)
                        finalName: controlName, // Log the final calculated name
                    });

                    return {
                        controlId: ctrl.controlId,
                        controlName: controlName, // Use the looked-up or fallback name
                        description: controlDescription, // Use the looked-up description
                        responsiblePerson: ctrl.responsiblePerson || '-',
                        decision: ctrl.decision,
                        decisionDate: ctrl.decisionDate,
                    };
                }),
                residualSeverity: getSeverityLevelInfo(entry.residualSeverity),
                gapComment: ruleGapComments[entry.ruleId] || '',
                deLabel: dreadedEvent?.businessValue === '__global__' ? 'Risque non intentionnel' : 'Événement redouté',
            };
        }).filter(Boolean); // Filter out entries where rule/DE couldn't be found

        // ---> ADD Sorting by Rule Name/ID <---
        processedPlan.sort((a, b) => {
            const ruleNameA = a.ruleName?.toLowerCase() || String(a.ruleId); // Use calculated name or ID
            const ruleNameB = b.ruleName?.toLowerCase() || String(b.ruleId);
            if (ruleNameA < ruleNameB) return -1;
            if (ruleNameA > ruleNameB) return 1;
            // Optional: secondary sort by DE name
            const deNameA = a.deName?.toLowerCase() || '';
            const deNameB = b.deName?.toLowerCase() || '';
            return deNameA.localeCompare(deNameB);
        });

        console.log("RT: Calculated and sorted detailedTreatmentPlan:", processedPlan);

        return processedPlan;
    }, [currentAnalysisControlPlan, currentSecurityFramework, currentDreadedEvents, allControls]);

    // --- NEW: Risk Distribution Calculations ---
    const initialRiskDistribution = useMemo(() => {
        const distribution = { 'G5': 0, 'G4': 0, 'G3': 0, 'G2': 0, 'G1': 0, 'N/A': 0 };
        detailedDreadedEvents.forEach(de => {
            const level = de.severityInfo.level;
            if (distribution.hasOwnProperty(level)) {
                distribution[level]++;
            } else {
                distribution['N/A']++; // Catch any unexpected levels
            }
        });
        return distribution;
    }, [detailedDreadedEvents]);

    const residualRiskDistribution = useMemo(() => {
        const distribution = { 'G5': 0, 'G4': 0, 'G3': 0, 'G2': 0, 'G1': 0, 'N/A': 0 };
        detailedTreatmentPlan.forEach(item => {
            const level = item.residualSeverity.level;
            if (distribution.hasOwnProperty(level)) {
                distribution[level]++;
            } else {
                distribution['N/A']++;
            }
        });
        return distribution;
    }, [detailedTreatmentPlan]);

    // --- NEW: Treatment Strategy Summary Calculation ---
    const treatmentStrategySummary = useMemo(() => {
        const summary = { reduce: 0, accept: 0, transfer: 0, avoid: 0, undefined: 0 };
        detailedTreatmentPlan.forEach(item => {
            // Use the direct value stored in the detailed plan item
            const actualOption = item.treatmentOptionValue || 'undefined';

            if (summary.hasOwnProperty(actualOption)) {
                summary[actualOption]++;
            } else {
                summary.undefined++;
            }
        });
        return summary;
    }, [detailedTreatmentPlan]);

    // --- NEW: Accepted Risks Calculation ---
    const acceptedRisks = useMemo(() => {
        const actualPlanObject = currentAnalysisControlPlan?.planData?.plan || {};
        return detailedTreatmentPlan
            .filter(item => {
                const planEntry = Object.values(actualPlanObject).find(entry => entry.ruleId === item.ruleId && entry.dreadedEventId === item.dreadedEventId);
                return planEntry?.treatmentOption === 'accept';
            });
    }, [detailedTreatmentPlan, currentAnalysisControlPlan]); // Added dependency

    // --- Checkbox Management Functions ---
    const handleRowSelect = useCallback((rowId, isSelected) => {
        setSelectedRows(prev => {
            const newSet = new Set(prev);
            if (isSelected) {
                newSet.add(rowId);
            } else {
                newSet.delete(rowId);
            }
            return newSet;
        });
    }, []);

    const handleSelectAll = useCallback((isSelected) => {
        if (isSelected) {
            const allRowIds = detailedTreatmentPlan.map((item, index) =>
                item.id || `${item.ruleId || 'rule'}-${item.deName || 'event'}-${index}`
            );
            setSelectedRows(new Set(allRowIds));
        } else {
            setSelectedRows(new Set());
        }
    }, [detailedTreatmentPlan]);

    const handleDeleteSelected = useCallback(() => {
        if (selectedRows.size === 0) return;

        const confirmDelete = window.confirm(
            `Êtes-vous sûr de vouloir supprimer ${selectedRows.size} ligne(s) sélectionnée(s) du plan de traitement ?`
        );

        if (confirmDelete) {
            setSelectedRows(new Set());
            showSuccessToast(`${selectedRows.size} ligne(s) supprimée(s) avec succès.`);
        }
    }, [selectedRows]);

    // --- Refresh Handler - Specifically refresh security framework and control plan data ---
    const handleRefreshData = useCallback(async () => {
        if (!currentAnalysis?.id) {
            console.error('No analysis selected for refresh');
            return;
        }

        try {
            console.log('Refreshing security framework and control plan data for risk treatment...');

            const result = await useAtelier1Store.getState().refreshFramework(currentAnalysis.id);

            if (result.success) {
                console.log('Security framework and control plan data refreshed successfully for risk treatment');
            } else {
                console.error('Failed to refresh security framework data:', result.message);
            }
        } catch (error) {
            console.error('Error refreshing security framework data:', error);
        }
    }, [currentAnalysis?.id, useAtelier1Store.getState().refreshFramework]);

    // --- Print Handler ---
    const handlePrintReport = useCallback(() => {
        const printArea = document.getElementById('risk-treatment-report-print-area');
        if (!printArea) {
            console.error("Print area '#risk-treatment-report-print-area' not found.");
            showErrorToast("Erreur: Impossible de trouver la zone d'impression. Veuillez contacter ACG pour assistance.");
            return;
        }

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`<html><head><title>${t('riskTreatment.title')}</title>`);
        printWindow.document.write(`
          <style>
            @media print {
              @page { size: A4; margin: 0.75in; }
              body {
                font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
                font-size: 9.5pt; /* Slightly smaller base */
                line-height: 1.4;
                color: #333;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                counter-reset: page;
              }
              @page {
                @bottom-right {
                  content: "Page " counter(page);
                  font-size: 8pt;
                  color: #666;
                }
              }
              h1 { font-size: 16pt; text-align: center; margin-bottom: 25px; color: #1e3a8a; font-weight: 600; }
              h2 { font-size: 13pt; margin-top: 20px; margin-bottom: 10px; border-bottom: 1px solid #ccc; padding-bottom: 4px; color: #1e40af; font-weight: 600; display: flex; align-items: center; }
              h2 svg { width: 18px; height: 18px; margin-right: 8px; color: #3b82f6; }
              h3 { font-size: 11pt; margin-top: 15px; margin-bottom: 5px; font-weight: 600; color: #1d4ed8; }
              p { margin-bottom: 6px; }
              strong { font-weight: 600; color: #1f2937; }
              ul { list-style: disc; margin-left: 25px; margin-bottom: 10px; }
              table { border-collapse: collapse; width: 100%; font-size: 8pt; margin-top: 8px; margin-bottom: 15px; table-layout: fixed; border: 1px solid #ddd; }
              th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word; }
              th { background-color: #f9fafb !important; font-weight: 600; color: #1f2937 !important; font-size: 7.5pt; text-transform: uppercase; letter-spacing: 0.03em; }
              tbody tr:nth-child(odd) { background-color: #f9fafb !important; } /* Zebra striping */
              .section-card { border: 1px solid #e5e7eb; border-radius: 0.375rem; padding: 12px 15px; margin-bottom: 18px; background-color: #fff !important; break-inside: avoid; }
              .detail-table { font-size: 8pt; margin-top: 10px; border: 1px solid #e5e7eb; width: 100%; table-layout: fixed; } /* Ensure full width */
              .detail-table th { background-color: #f3f4f6 !important; font-size: 7pt; padding: 5px 7px; }
              .detail-table td { padding: 5px 7px; font-size: 7.5pt; }
              .bv-asset-list { list-style: none; padding-left: 15px; margin-top: 5px; }
              .bv-asset-list li { margin-bottom: 3px; font-size: 7.5pt; }
              .bv-asset-list strong { color: #4b5563; }
              .dreaded-event-table th:nth-child(1) { width: 20%; } /* Name */
              .dreaded-event-table th:nth-child(2) { width: 30%; } /* Description */
              .dreaded-event-table th:nth-child(3) { width: 20%; } /* Business Value */
              .dreaded-event-table th:nth-child(4) { width: 15%; } /* Impact Principal */
              .dreaded-event-table th:nth-child(5) { width: 15%; text-align: center; } /* Severity */
              .security-rule-table th:nth-child(1) { width: 25%; } /* Name */
              .security-rule-table th:nth-child(2) { width: 30%; } /* Description */
              .security-rule-table th:nth-child(3) { width: 30%; } /* Linked DEs */
              .security-rule-table th:nth-child(4) { width: 25%; } /* Status */
              .severity-box-print { display: inline-flex; flex-direction: column; align-items: center; justify-content: center; padding: 0.1rem 0.15rem; border-radius: 0.25rem; font-size: 0.55rem; font-weight: 600; text-align: center; border: 1px solid #ccc; width: 3.5rem; height: 2.2rem; box-sizing: border-box; vertical-align: middle; }
              .severity-box-print span { display: block !important; line-height: 1 !important; }
              .severity-box-print .label-print { font-size: 0.5rem !important; line-height: 1 !important; margin-top: 1px; }
              /* Severity Colors */
              .bg-black-print { background-color: #000 !important; color: #fff !important; }
              .text-white-print { color: #fff !important; }
              .bg-red-600-print { background-color: #DC2626 !important; color: #fff !important; }
              .bg-yellow-500-print { background-color: #F59E0B !important; color: #000 !important; }
              .text-black-print { color: #000 !important; }
              .bg-yellow-200-print { background-color: #FEF08A !important; color: #000 !important; }
              .bg-cyan-300-print { background-color: #67E8F9 !important; color: #000 !important; }
              .bg-gray-200-print { background-color: #E5E7EB !important; color: #374151 !important; }
              /* --- Treatment Plan Table Specifics (Print) --- */
              .treatment-plan-table { font-size: 6.5pt; }
              .treatment-plan-table th, .treatment-plan-table td { padding: 2px 3px; } /* Minimal padding */
              .treatment-plan-table th:nth-child(1) { width: 10%; } /* Règle */
              .treatment-plan-table th:nth-child(2) { width: 10%; } /* Événement */
              .treatment-plan-table th:nth-child(3) { width: 12%; } /* Impact */
              .treatment-plan-table th:nth-child(4) { width: 11%; }  /* Grav. Init. */
              .treatment-plan-table th:nth-child(5) { width: 16%; } /* Contrôles */
              .treatment-plan-table th:nth-child(6) { width: 11%; }  /* Grav. Résid. */
              .treatment-plan-table th:nth-child(7) { width: 8%; }  /* Réduction % */
              .control-list-item { margin-bottom: 4px; line-height: 1.3; }
              .control-list-item strong { font-weight: 600; margin-right: 4px; color: #4b5563; }
              .control-list-item .label { display: inline-block; width: 60px; } /* Align Resp/Decision labels */
              .control-list-item .rationale { display: block; font-style: italic; color: #6b7280; padding-left: 65px; font-size: 0.9em; }
              .no-print { display: none !important; } /* Hide print button in print */
            }
          </style>
        `);
        printWindow.document.write('</head><body>');
        printWindow.document.write(`<h1>${t('riskTreatment.title')}</h1>`); // Updated Title

        const contentClone = printArea.cloneNode(true);

        // Apply print classes to severity boxes
        contentClone.querySelectorAll('.severity-box').forEach(box => {
            box.classList.add('severity-box-print');
            const bgColorClass = Array.from(box.classList).find(cls => cls.startsWith('bg-'));
            if (bgColorClass) box.classList.add(`${bgColorClass}-print`);
            const textColorClass = Array.from(box.classList).find(cls => cls.startsWith('text-'));
            if (textColorClass) box.classList.add(`${textColorClass}-print`);
            const labelSpan = box.querySelector('.label');
            if (labelSpan) labelSpan.classList.add('label-print');
        });

        // Add SVG icons to H2 headers in the clone (using simple path data for print)
        const iconMap = {
            'Informations Générales': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18" style="margin-right: 8px; vertical-align: middle;"><path d="M9.25 13.25a.75.75 0 001.5 0V4.75l.97 1.029a.75.75 0 001.091-1.029l-2.25-2.368a.75.75 0 00-1.062 0l-2.25 2.368a.75.75 0 101.091 1.029L9.25 4.75v8.5z"/><path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z"/></svg>',
            'Contexte Métier': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18" style="margin-right: 8px; vertical-align: middle;"><path d="M2.879 7.121A3 3 0 017.5 6.001h5a3 3 0 014.621 1.12l1.112 2.224A4.5 4.5 0 0117.5 13.5V15a3 3 0 01-3 3h-9a3 3 0 01-3-3v-1.5a4.5 4.5 0 01-.732-4.155l1.111-2.224zM17 13.5V15a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 015 15v-1.5a3 3 0 01-.18-5.996l-1.11-2.223a1.5 1.5 0 012.31-1.152l.318.159A3 3 0 017.5 7.501h5a3 3 0 012.682 1.62l.318-.16a1.5 1.5 0 012.31 1.153l-1.11 2.223A3 3 0 0117 13.5z"/></svg>',
            'Identification des Risques': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18" style="margin-right: 8px; vertical-align: middle;"><path fill-rule="evenodd" d="M8.485 2.47a.75.75 0 011.03 0l6.25 6.25a.75.75 0 01-.023 1.053l-6.25 6.25a.75.75 0 01-1.03 0l-6.25-6.25a.75.75 0 01.023-1.053l6.25-6.25zm.76 8.78a.75.75 0 10-1.5 0v2a.75.75 0 001.5 0v-2zm-.75-3.5a.75.75 0 00-.75.75v.75c0 .414.336.75.75.75h.01a.75.75 0 00.75-.75v-.75a.75.75 0 00-.75-.75H9.245z" clip-rule="evenodd"/></svg>',
            'Socle de Sécurité': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18" style="margin-right: 8px; vertical-align: middle;"><path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd"/></svg>',
            'Menaces Identifiées': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18" style="margin-right: 8px; vertical-align: middle;"><path fill-rule="evenodd" d="M11.06 2.148a.75.75 0 00-1.06-.04l-7.5 7.5a.75.75 0 101.06 1.06L10 4.268l6.44 6.44a.75.75 0 101.06-1.06l-7.5-7.5a.75.75 0 00-.94-.04zM10 12.75a.75.75 0 00-.75.75v4a.75.75 0 001.5 0v-4a.75.75 0 00-.75-.75z" clip-rule="evenodd" /></svg>',
            'Vulnérabilités Identifiées': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18" style="margin-right: 8px; vertical-align: middle;"><path fill-rule="evenodd" d="M10.313 4.042a3.5 3.5 0 10-5.626 4.007l-.687.687a.75.75 0 000 1.06l1.414 1.414a.75.75 0 001.06 0l.687-.687a3.502 3.502 0 004.006-5.626L10.313 4.042zm4.95 4.95a3.5 3.5 0 10-5.627-4.007l.687-.687a.75.75 0 00-1.06-1.06L7.85 4.654a.75.75 0 000 1.06l.687.687A3.5 3.5 0 0012.542 12l.854.854a.75.75 0 001.06 0l1.414-1.414a.75.75 0 000-1.06l-.853-.854-.001-.001zM8.464 11.536l-1.414 1.414a.75.75 0 000 1.06l2.121 2.121a.75.75 0 001.06 0L12.35 14.01a3.51 3.51 0 00-3.886-2.474z" clip-rule="evenodd" /></svg>',
            'Scénarios de Risque': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18" style="margin-right: 8px; vertical-align: middle;"><path fill-rule="evenodd" d="M7.84 1.82a.75.75 0 01.84.2l5.25 6.5a.75.75 0 01-.114 1.09l-5.25 4.2a.75.75 0 01-1.166-.71V2.63a.75.75 0 01.49-.71zm-.23 1.88v12.1a.75.75 0 001.165.71l5.25-4.2a.75.75 0 00.115-1.09l-5.25-6.5a.75.75 0 00-1.315-.93l.035.03v-.05zM12.25 7a.75.75 0 000 1.5h3a.75.75 0 000-1.5h-3z" clip-rule="evenodd" /></svg>',
            'Distribution Initiale des Risques': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18" style="margin-right: 8px; vertical-align: middle;"><path d="M15.5 2.75a.75.75 0 00-1.5 0v14.5a.75.75 0 001.5 0V2.75zM10.5 10a.75.75 0 00-1.5 0v7.25a.75.75 0 001.5 0V10zM5.5 5.75a.75.75 0 00-1.5 0v11.5a.75.75 0 001.5 0V5.75z" /></svg>',
            'Plan de Traitement Détaillé': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18" style="margin-right: 8px; vertical-align: middle;"><path fill-rule="evenodd" d="M16.403 3.903a.75.75 0 01.07 1.057l-4.75 8.25a.75.75 0 01-1.14.17L7.87 10.63a.75.75 0 01-1.118 1.002l-3.75-2.25a.75.75 0 01.368-1.384l3.588 1.196 2.064-2.89a.75.75 0 011.14-.17l4.75 8.25a.75.75 0 011.14-.17l4.25-7.5a.75.75 0 011.118 1.002l-5 3a.75.75 0 11-.736-1.224L16.403 3.903zM4.75 16.75a.75.75 0 01.75-.75h9.5a.75.75 0 010 1.5h-9.5a.75.75 0 01-.75-.75z" clip-rule="evenodd"/></svg>',
        };
        contentClone.querySelectorAll('h2').forEach(h2 => {
            const title = h2.textContent.trim();
            if (iconMap[title]) {
                h2.innerHTML = iconMap[title] + h2.innerHTML; // Prepend SVG string
            }
        });

        printWindow.document.write(contentClone.innerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
        }, 500);
    }, [detailedTreatmentPlan, detailedBusinessValues, detailedDreadedEvents, detailedSecurityRules, analysisOverview, participantsList, t]);

    // --- PDF Export Handler ---
    const handleExportPDF = useCallback(async () => {
        try {
            // Prepare report data - Full Workshop 1
            const reportData = {
                analysisName: analysisOverview.name,
                organizationName: analysisOverview.orgName,
                scope: analysisOverview.scope,
                missions: analysisOverview.missions,
                contraintes: analysisOverview.contraintes,
                hypotheses: analysisOverview.hypotheses,
                participants: participantsList,
                responsiblePersonsList: currentContextData?.responsiblePersonsList || [],
                businessValuesCount: detailedBusinessValues.length,
                dreadedEventsCount: detailedDreadedEvents.length,
                securityRulesCount: detailedSecurityRules.length,
                criticalRisksCount: initialRiskDistribution['G5'] + initialRiskDistribution['G4'],
                initialRiskDistribution: initialRiskDistribution,
                detailedBusinessValues: detailedBusinessValues,
                detailedDreadedEvents: detailedDreadedEvents,
                detailedSecurityRules: detailedSecurityRules,
                detailedTreatmentPlan: detailedTreatmentPlan,
                controlPlan: currentAnalysisControlPlan,
                securityFramework: currentSecurityFramework,
                dreadedEvents: currentDreadedEvents
            };

            // Import PDF generator dynamically
            const { default: RiskTreatmentPdfGenerator } = await import('../../../utils/riskTreatmentPdfGenerator.js');

            // Create PDF generator instance
            const pdfGenerator = new RiskTreatmentPdfGenerator(reportData, {
                name: analysisOverview.name,
                companyName: analysisOverview.orgName,
                createdBy: 'Système EBIOS RM'
            }, {
                controlPlan: currentAnalysisControlPlan,
                securityFramework: currentSecurityFramework,
                dreadedEvents: currentDreadedEvents
            });

            // Generate PDF
            const pdfBlob = await pdfGenerator.generateRiskTreatmentReport();

            // Download PDF
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `EBIOS-RM_Traitement-Risques_${analysisOverview.orgName || 'Organisation'}_${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 0);

            console.log('PDF généré avec succès');
        } catch (error) {
            console.error('Error exporting PDF:', error);
            showErrorToast("Erreur lors de l'export PDF. Veuillez contacter ACG pour assistance.");
        }
    }, [analysisOverview, detailedBusinessValues, detailedDreadedEvents, detailedSecurityRules, detailedTreatmentPlan, initialRiskDistribution, participantsList, currentAnalysisControlPlan, currentSecurityFramework, currentDreadedEvents]);


    // --- PDF Export Handler (English) ---
    const handleExportPDFEn = useCallback(async () => {
        try {
            // Prepare report data - Full Workshop 1
            const reportData = {
                analysisName: analysisOverview.name,
                organizationName: analysisOverview.orgName,
                scope: analysisOverview.scope,
                missions: analysisOverview.missions,
                contraintes: analysisOverview.contraintes,
                hypotheses: analysisOverview.hypotheses,
                participants: participantsList,
                responsiblePersonsList: currentContextData?.responsiblePersonsList || [],
                businessValuesCount: detailedBusinessValues.length,
                dreadedEventsCount: detailedDreadedEvents.length,
                securityRulesCount: detailedSecurityRules.length,
                criticalRisksCount: initialRiskDistribution['G5'] + initialRiskDistribution['G4'],
                initialRiskDistribution: initialRiskDistribution,
                detailedBusinessValues: detailedBusinessValues,
                detailedDreadedEvents: detailedDreadedEvents,
                detailedSecurityRules: detailedSecurityRules,
                detailedTreatmentPlan: detailedTreatmentPlan,
                controlPlan: currentAnalysisControlPlan,
                securityFramework: currentSecurityFramework,
                dreadedEvents: currentDreadedEvents
            };

            // Import bilingual PDF generator dynamically (English)
            const { default: RiskTreatmentPdfGenerator } = await import('../../../utils/riskTreatmentPdfGenerator.js');

            // Create PDF generator instance
            const pdfGenerator = new RiskTreatmentPdfGenerator(reportData, {
                name: analysisOverview.name,
                companyName: analysisOverview.orgName,
                createdBy: 'EBIOS RM System'
            }, {
                controlPlan: currentAnalysisControlPlan,
                securityFramework: currentSecurityFramework,
                dreadedEvents: currentDreadedEvents
            }, 'en');

            // Generate PDF
            const pdfBlob = await pdfGenerator.generateRiskTreatmentReport();

            // Download PDF
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `EBIOS-RM_Risk-Treatment_${analysisOverview.orgName || 'Organization'}_${new Date().toISOString().slice(0, 10)}_EN.pdf`;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 0);

            console.log('PDF (EN) generated successfully');
        } catch (error) {
            console.error('Error exporting PDF (EN):', error);
            showErrorToast('Error exporting PDF. Please contact ACG for assistance.');
        }
    }, [analysisOverview, detailedBusinessValues, detailedDreadedEvents, detailedSecurityRules, detailedTreatmentPlan, initialRiskDistribution, participantsList, currentAnalysisControlPlan, currentSecurityFramework, currentDreadedEvents]);

    // --- CSV Export Handler (French) ---
    const handleExportCSV = useCallback(async () => {
        try {
            const { default: RiskTreatmentCsvGenerator } = await import('../../../utils/riskTreatmentCsvGenerator.js');
            const reportData = {
                analysisName: analysisOverview.name,
                organizationName: analysisOverview.orgName,
                scope: analysisOverview.scope,
                missions: analysisOverview.missions,
                contraintes: analysisOverview.contraintes,
                hypotheses: analysisOverview.hypotheses,
                participants: participantsList,
                responsiblePersonsList: currentContextData?.responsiblePersonsList || [],
                detailedBusinessValues: detailedBusinessValues,
                detailedDreadedEvents: detailedDreadedEvents,
                detailedSecurityRules: detailedSecurityRules,
                detailedTreatmentPlan: detailedTreatmentPlan,
                initialRiskDistribution: initialRiskDistribution
            };
            const csvGenerator = new RiskTreatmentCsvGenerator(reportData, 'fr');
            const filename = `EBIOS-RM_Rapport_Atelier1_${analysisOverview.orgName || 'Organisation'}_${new Date().toISOString().slice(0, 10)}.xls`;
            csvGenerator.download(filename);
            console.log('CSV généré avec succès');
        } catch (error) {
            console.error('Error exporting CSV:', error);
            showErrorToast("Erreur lors de l'export CSV. Veuillez contacter ACG pour assistance.");
        }
    }, [analysisOverview, detailedBusinessValues, detailedDreadedEvents, detailedSecurityRules, detailedTreatmentPlan, initialRiskDistribution, participantsList, currentContextData]);

    // --- CSV Export Handler (English) ---
    const handleExportCSVEn = useCallback(async () => {
        try {
            const { default: RiskTreatmentCsvGenerator } = await import('../../../utils/riskTreatmentCsvGenerator.js');
            const reportData = {
                analysisName: analysisOverview.name,
                organizationName: analysisOverview.orgName,
                scope: analysisOverview.scope,
                missions: analysisOverview.missions,
                contraintes: analysisOverview.contraintes,
                hypotheses: analysisOverview.hypotheses,
                participants: participantsList,
                responsiblePersonsList: currentContextData?.responsiblePersonsList || [],
                detailedBusinessValues: detailedBusinessValues,
                detailedDreadedEvents: detailedDreadedEvents,
                detailedSecurityRules: detailedSecurityRules,
                detailedTreatmentPlan: detailedTreatmentPlan,
                initialRiskDistribution: initialRiskDistribution
            };
            const csvGenerator = new RiskTreatmentCsvGenerator(reportData, 'en');
            const filename = `EBIOS-RM_Report_Workshop1_${analysisOverview.orgName || 'Organization'}_${new Date().toISOString().slice(0, 10)}.xls`;
            csvGenerator.download(filename);
            console.log('CSV (EN) generated successfully');
        } catch (error) {
            console.error('Error exporting CSV (EN):', error);
            showErrorToast('Error exporting CSV. Please contact ACG for assistance.');
        }
    }, [analysisOverview, detailedBusinessValues, detailedDreadedEvents, detailedSecurityRules, detailedTreatmentPlan, initialRiskDistribution, participantsList, currentContextData]);

    // --- DOCX Export Handler ---
    const handleExportDocx = useCallback(async () => {
        try {
            showLoadingToast('Génération du rapport Word...');
            const { generateRiskTreatmentDocx } = await import('../../../utils/riskTreatmentDocxGenerator.js');
            const reportData = {
                analysisName: analysisOverview.name,
                organizationName: analysisOverview.orgName,
                scope: analysisOverview.scope,
                missions: analysisOverview.missions,
                contraintes: analysisOverview.contraintes,
                hypotheses: analysisOverview.hypotheses,
                participants: participantsList,
                responsiblePersonsList: currentContextData?.responsiblePersonsList || [],
                detailedBusinessValues: detailedBusinessValues,
                detailedDreadedEvents: detailedDreadedEvents,
                detailedSecurityRules: detailedSecurityRules,
                detailedTreatmentPlan: detailedTreatmentPlan,
                initialRiskDistribution: initialRiskDistribution
            };
            await generateRiskTreatmentDocx(reportData, {
                name: analysisOverview.name,
                companyName: analysisOverview.orgName
            });
            showSuccessToast('Rapport Word généré avec succès');
        } catch (error) {
            console.error('Error exporting Word:', error);
            showErrorToast('Erreur lors de la génération du document Word');
        }
    }, [currentAnalysis, analysisOverview, participantsList, currentContextData, detailedBusinessValues, detailedDreadedEvents, detailedSecurityRules, detailedTreatmentPlan, initialRiskDistribution]);

    // --- DOCX Export Handler (English) ---
    const handleExportDocxEn = useCallback(async () => {
        try {
            showLoadingToast('Generating Word report...');
            const { generateRiskTreatmentDocx } = await import('../../../utils/riskTreatmentDocxGenerator.js');
            const reportData = {
                analysisName: analysisOverview.name,
                organizationName: analysisOverview.orgName,
                scope: analysisOverview.scope,
                missions: analysisOverview.missions,
                contraintes: analysisOverview.contraintes,
                hypotheses: analysisOverview.hypotheses,
                participants: participantsList,
                responsiblePersonsList: currentContextData?.responsiblePersonsList || [],
                detailedBusinessValues: detailedBusinessValues,
                detailedDreadedEvents: detailedDreadedEvents,
                detailedSecurityRules: detailedSecurityRules,
                detailedTreatmentPlan: detailedTreatmentPlan,
                initialRiskDistribution: initialRiskDistribution
            };
            await generateRiskTreatmentDocx(reportData, {
                name: analysisOverview.name,
                companyName: analysisOverview.orgName
            }, 'en');
            showSuccessToast('Word report generated successfully');
        } catch (error) {
            console.error('Error exporting Word (EN):', error);
            showErrorToast('Error generating Word document (EN)');
        }
    }, [currentAnalysis, analysisOverview, participantsList, currentContextData, detailedBusinessValues, detailedDreadedEvents, detailedSecurityRules, detailedTreatmentPlan, initialRiskDistribution]);


    // --- Render Logic ---
    if (analysisStore.isWorkshopDataLoading) {
        return <div className="p-6 text-center">Chargement des données pour la synthèse...</div>;
    }

    if (analysisStore.workshopDataError) {
        return <div className="p-6 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <p className="font-semibold">Erreur de chargement des données de synthèse:</p>
            <pre className="mt-2 text-xs whitespace-pre-wrap">{JSON.stringify(analysisStore.workshopDataError, null, 2)}</pre>
        </div>;
    }

    if (!currentAnalysis) {
        return <div className="p-6 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-lg">Sélectionnez une analyse pour voir la synthèse.</div>;
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="p-6 bg-white rounded-lg shadow-sm border border-gray-200"
        >
            {/* === MERGED HEADER === */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    {/* Left Side: Breadcrumb & Title */}
                    <div>
                        <div className="flex items-center text-sm text-slate-500 mb-2">
                            <span className="hover:text-blue-600 transition-colors">{t('riskTreatment.breadcrumb.workshop1')}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span className="text-blue-600 font-medium">{t('riskTreatment.breadcrumb.synthesis')}</span>
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center">
                            <FileText size={28} className="mr-3 text-blue-600" />
                            {t('riskTreatment.title')}
                        </h1>
                    </div>

                    {/* Right Side: Action Buttons */}
                    <div className="flex items-center space-x-3">
                        {/* Refresh Button */}
                        <Button
                            variant="secondary"
                            color="blue"
                            icon={<RefreshCw />}
                            onClick={handleRefreshData}
                            disabled={analysisStore.isWorkshopDataLoading}
                            loading={analysisStore.isWorkshopDataLoading}
                            title={t('riskTreatment.tooltips.refresh')}
                        >
                            {analysisStore.isWorkshopDataLoading ? t('riskTreatment.buttons.refreshing') : t('riskTreatment.buttons.refresh')}
                        </Button>

                        {/* Consolidated Export Dropdown */}
                        <div className="relative">
                            <button
                                className="text-sm font-medium bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center shadow-sm transition duration-200"
                                onClick={() => setIsPdfMenuOpen(!isPdfMenuOpen)}
                            >
                                <FileText size={16} className="mr-2" />
                                {t('common.export', 'Exporter')}
                                <ChevronDown size={14} className={`ml-1 transition-transform duration-200 ${isPdfMenuOpen ? 'rotate-180' : ''}`} />
                            </button>
                            <AnimatePresence>
                                {isPdfMenuOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-slate-100 overflow-hidden z-20 origin-top-right"
                                    >
                                        <div className="px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 border-b border-slate-100 uppercase tracking-wider">
                                            PDF Export
                                        </div>
                                        <button
                                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-b border-slate-100 flex items-center"
                                            onClick={() => {
                                                handleExportPDF();
                                                setIsPdfMenuOpen(false);
                                            }}
                                        >
                                            <span className="mr-2">🇫🇷</span> Français (PDF)
                                        </button>
                                        <button
                                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-b border-slate-100 flex items-center"
                                            onClick={() => {
                                                handleExportPDFEn();
                                                setIsPdfMenuOpen(false);
                                            }}
                                        >
                                            <span className="mr-2">🇬🇧</span> English (PDF)
                                        </button>

                                        <div className="px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 border-b border-slate-100 uppercase tracking-wider">
                                            Word Export
                                        </div>
                                        <button
                                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-b border-slate-100 flex items-center"
                                            onClick={() => {
                                                handleExportDocx();
                                                setIsPdfMenuOpen(false);
                                            }}
                                        >
                                            <span className="mr-2">🇫🇷</span> Français (Word)
                                        </button>
                                        <button
                                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center"
                                            onClick={() => {
                                                handleExportDocxEn();
                                                setIsPdfMenuOpen(false);
                                            }}
                                        >
                                            <span className="mr-2">🇬🇧</span> English (Word)
                                        </button>

                                        <div className="px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 border-b border-slate-100 uppercase tracking-wider">
                                            CSV Export
                                        </div>
                                        <button
                                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-b border-slate-100 flex items-center"
                                            onClick={() => {
                                                handleExportCSV();
                                                setIsPdfMenuOpen(false);
                                            }}
                                        >
                                            <span className="mr-2">🇫🇷</span> Français (CSV)
                                        </button>
                                        <button
                                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center"
                                            onClick={() => {
                                                handleExportCSVEn();
                                                setIsPdfMenuOpen(false);
                                            }}
                                        >
                                            <span className="mr-2">🇬🇧</span> English (CSV)
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
            {/* === END MERGED HEADER === */}

            {/* Report Content Area for Printing */}
            <div id="risk-treatment-report-print-area">

                {/* --- SYNTHÈSE EXÉCUTIVE --- */}
                <div className="section-card bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
                    <h2 className="text-xl font-semibold text-blue-700 border-b border-gray-200 pb-2 mb-4 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="20" height="20" className="mr-2 text-blue-600"><path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z"/><path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 010-1.186A10.004 10.004 0 0110 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 17c-4.257 0-7.893-2.66-9.336-6.41zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
                        {t('riskTreatment.executiveSummaryTitle')}
                    </h2>

                    <div className="space-y-4 text-sm text-gray-700">
                        <p className="text-gray-700">
                            {t('riskTreatment.executiveSummary.intro', {
                                analysisName: analysisOverview.name,
                                scope: analysisOverview.scope
                            })}
                        </p>

                        <p className="text-gray-700">
                            {t('riskTreatment.executiveSummary.businessValues', {
                                businessValuesCount: detailedBusinessValues.length,
                                dreadedEventsCount: detailedDreadedEvents.length,
                                criticalEventsCount: initialRiskDistribution['G5'] + initialRiskDistribution['G4']
                            })}
                        </p>

                        <p className="text-gray-700 mb-4">
                            {t('riskTreatment.executiveSummary.securityRules', {
                                rulesCount: detailedSecurityRules.length,
                                reduceCount: treatmentStrategySummary.reduce,
                                acceptCount: treatmentStrategySummary.accept,
                                transferCount: treatmentStrategySummary.transfer,
                                avoidCount: treatmentStrategySummary.avoid
                            })}
                            {treatmentStrategySummary.undefined > 0 && (
                                <span className="text-gray-600 ml-2">
                                    ({t('riskTreatment.executiveSummary.undefined', { count: treatmentStrategySummary.undefined })})
                                </span>
                            )}
                        </p>

                        <p className="text-gray-700">
                            {t('riskTreatment.executiveSummary.objective')}
                        </p>
                    </div>

                    {/* Métriques Clés */}
                    <div className="mt-6 pt-4 border-t border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-800 mb-3">{t('riskTreatment.metrics.title')}</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                                <div className="text-xl font-bold text-blue-700 mb-1">{detailedBusinessValues.length}</div>
                                <div className="text-xs text-gray-600">{t('riskTreatment.metrics.businessValues')}</div>
                            </div>
                            <div className="text-center p-3 bg-red-50 rounded-lg border border-red-100">
                                <div className="text-xl font-bold text-red-700 mb-1">{detailedDreadedEvents.length}</div>
                                <div className="text-xs text-gray-600">{t('riskTreatment.metrics.dreadedEvents')}</div>
                            </div>
                            <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
                                <div className="text-xl font-bold text-green-700 mb-1">{detailedSecurityRules.length}</div>
                                <div className="text-xs text-gray-600">{t('riskTreatment.metrics.securityRules')}</div>
                            </div>
                            <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-100">
                                <div className="text-xl font-bold text-orange-700 mb-1">{initialRiskDistribution['G5'] + initialRiskDistribution['G4']}</div>
                                <div className="text-xs text-gray-600">{t('riskTreatment.metrics.criticalRisks')}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section: Analysis Overview */}
                <div className="section-card bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
                    <h2 className="text-xl font-semibold text-blue-700 border-b border-gray-200 pb-2 mb-4 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="20" height="20" className="mr-2 text-blue-600"><path d="M9.25 13.25a.75.75 0 001.5 0V4.75l.97 1.029a.75.75 0 001.091-1.029l-2.25-2.368a.75.75 0 00-1.062 0l-2.25 2.368a.75.75 0 101.091 1.029L9.25 4.75v8.5z"/><path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z"/></svg>
                        {t('riskTreatment.generalInfo')}
                    </h2>

                    <div className="space-y-4 text-sm text-gray-700">
                        <p className="text-gray-700">
                            <strong className="text-blue-700">{t('riskTreatment.analysisName')} :</strong> {analysisOverview.name}
                        </p>
                        <p className="text-gray-700">
                            <strong className="text-blue-700">{t('riskTreatment.creationDate')} :</strong> {analysisOverview.date}
                        </p>
                        <p className="text-gray-700">
                            <strong className="text-blue-700">{t('riskTreatment.organization')} :</strong> {analysisOverview.orgName}
                        </p>
                        <p className="text-gray-700">
                            <strong className="text-blue-700">{t('riskTreatment.scope')} :</strong> {analysisOverview.scope}
                        </p>

                        {analysisOverview.hypotheses.length > 0 && (
                            <div className="mt-2">
                                <p className="text-gray-700">
                                    <strong className="text-blue-700">{t('riskTreatment.hypotheses')} :</strong>{' '}
                                    {analysisOverview.hypotheses.map((h, i) => (
                                        <span key={i}>{i > 0 && '; '}{h}</span>
                                    ))}
                                </p>
                            </div>
                        )}

                        {analysisOverview.contraintes.length > 0 && (
                            <div className="mt-2">
                                <p className="text-gray-700">
                                    <strong className="text-blue-700">{t('riskTreatment.constraints')} :</strong>{' '}
                                    {analysisOverview.contraintes.map((c, i) => (
                                        <span key={i}>{i > 0 && '; '}{c}</span>
                                    ))}
                                </p>
                            </div>
                        )}

                        {analysisOverview.missions.length > 0 && (
                            <div className="mt-2">
                                <p className="text-gray-700">
                                    <strong className="text-blue-700">{t('riskTreatment.missions')} :</strong>{' '}
                                    {analysisOverview.missions.map((m, i) => (
                                        <span key={i}>{i > 0 && '; '}{m}</span>
                                    ))}
                                </p>
                            </div>
                        )}

                        {participantsList.length > 0 && (
                            <div className="mt-2">
                                <p className="text-gray-700">
                                    <strong className="text-blue-700">{t('riskTreatment.participants')} :</strong>{' '}
                                    {participantsList.map((p, i) => (
                                        <span key={p.id}>{i > 0 && '; '}{p.name} ({p.position === 'OTHER' ? (p.customPosition || 'N/A') : (p.position || 'N/A')})</span>
                                    ))}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* --- NEW: Initial Risk Distribution --- */}
                <div className="section-card bg-white p-6 rounded-lg shadow-md mb-6">
                    <h2 className="text-xl font-semibold text-orange-700 border-b border-gray-300 pb-2 mb-4 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18" className="mr-2 text-orange-600"><path d="M15.5 2.75a.75.75 0 00-1.5 0v14.5a.75.75 0 001.5 0V2.75zM10.5 10a.75.75 0 00-1.5 0v7.25a.75.75 0 001.5 0V10zM5.5 5.75a.75.75 0 00-1.5 0v11.5a.75.75 0 001.5 0V5.75z" /></svg>
                        {t('riskTreatment.initialDistribution.title')}
                    </h2>
                    <p className="text-sm mb-4 text-gray-700">{t('riskTreatment.initialDistribution.description', { count: detailedDreadedEvents.length })}</p>
                    <div className="overflow-x-auto -mx-6 px-6">
                        <table className="min-w-full detail-table text-xs border border-gray-200 w-3/4 mx-auto"> {/* Centered & Smaller Table */}
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-2 font-medium text-gray-600 w-1/2">{t('riskTreatment.table.headers.severityLevel')}</th>
                                    <th className="px-3 py-2 font-medium text-gray-600 w-1/2 text-center">{t('riskTreatment.table.headers.eventCount')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {Object.entries(initialRiskDistribution)
                                    // Show levels with count > 0, always show N/A if present
                                    .filter(([level, count]) => count > 0 || level === 'N/A')
                                    .map(([level, count]) => (
                                        <tr key={level} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 text-gray-700">
                                                {(() => {
                                                    // Use the reliable hardcoded map
                                                    const levelLabelMap = {
                                                        'G5': t('severity.catastrophic'), 'G4': t('severity.critical'), 'G3': t('severity.major'),
                                                        'G2': t('severity.moderate'), 'G1': t('severity.minor'), 'N/A': 'Non défini'
                                                    };
                                                    const label = levelLabelMap[level] || level; // Fallback to level code if not found
                                                    return `${label} (${level})`;
                                                })()}
                                            </td>
                                            <td className="px-4 py-2 text-gray-700 text-center font-semibold">{count}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Section: Business Context */}
                <div className="section-card bg-white p-6 rounded-lg shadow-md mb-6">
                    <h2 className="text-xl font-semibold text-green-700 border-b border-gray-300 pb-2 mb-4 flex items-center">
                        <Briefcase size={20} className="mr-2 text-green-600" />{t('riskTreatment.businessContext')}
                    </h2>
                    {detailedBusinessValues.length > 0 ? (
                        <div className="space-y-6"> {/* Main container for cards */}
                            {detailedBusinessValues.map(bv => (
                                <div key={bv.id} className="bg-slate-50 border border-slate-200 rounded-lg p-4 transition-shadow hover:shadow-md">
                                    {/* Business Value Header */}
                                    <div className="flex items-center mb-3">
                                        <div className="bg-green-100 p-2 rounded-full mr-3">
                                            <Briefcase className="h-5 w-5 text-green-600" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-slate-800">{bv.name}</h3>
                                            <p className="text-sm text-slate-600">{bv.description || t('common.noDescription')}</p>
                                        </div>
                                    </div>

                                    {/* Support Assets Section */}
                                    {bv.assets.length > 0 ? (
                                        <div className="pl-4 ml-5 border-l-2 border-green-200">
                                            <h4 className="text-sm font-semibold text-slate-700 mb-2">{t('riskTreatment.businessContextDetails.supportAssets', { count: bv.assets.length })}</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                                                {bv.assets.map(asset => (
                                                    <div key={asset.id} className="bg-white p-2 rounded-md border border-gray-200 text-xs">
                                                        <p className="font-semibold text-slate-800">{asset.name || `Asset ${asset.id}`}</p>
                                                        <p className="text-slate-500">
                                                            <span className="font-medium">{t('riskTreatment.businessContextDetails.type')}:</span> {asset.type || 'N/A'}
                                                        </p>
                                                        {asset.location && (
                                                            <p className="text-slate-500">
                                                                <span className="font-medium">{t('riskTreatment.businessContextDetails.location')}:</span> {asset.location}
                                                            </p>
                                                        )}
                                                        {asset.description && asset.description !== t('common.noDescription') && (
                                                            <p className="text-slate-500 italic mt-1">"{asset.description}"</p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="pl-4 ml-5 border-l-2 border-gray-200">
                                            <p className="text-sm text-gray-500 italic">{t('riskTreatment.businessContextDetails.noSupportAssets')}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 italic text-center py-4">{t('riskTreatment.businessContextDetails.noBusinessValues')}</p>
                    )}
                </div>

                {/* Section: Risk Identification -> Split DEs / Non-Intentional */}
                {(() => {
                    const dreadedEvents = detailedDreadedEvents.filter(de => !de.isNonIntentional);
                    const nonIntentionalRisks = detailedDreadedEvents.filter(de => de.isNonIntentional);
                    return (<>
                {dreadedEvents.length > 0 && (
                <div className="section-card bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
                    <h2 className="text-xl font-semibold text-red-700 border-b border-gray-300 pb-2 mb-4 flex items-center">
                        <AlertTriangle size={20} className="mr-2 text-red-600" />{t('riskTreatment.riskIdentification')}
                    </h2>
                    <div className="overflow-x-auto -mx-6 px-6">
                        <table className="min-w-full detail-table dreaded-event-table text-xs border border-gray-200">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-2 font-medium text-gray-600">{t('riskTreatment.table.headers.eventName')}</th>
                                    <th className="px-3 py-2 font-medium text-gray-600">{t('common.description')}</th>
                                    <th className="px-3 py-2 font-medium text-gray-600">{t('riskTreatment.table.headers.associatedBusinessValue')}</th>
                                    <th className="px-3 py-2 font-medium text-gray-600">{t('riskTreatment.table.headers.primaryImpact')}</th>
                                    <th className="px-3 py-2 font-medium text-gray-600 text-center">{t('riskTreatment.table.headers.initialSeverity')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {dreadedEvents.map((de) => (
                                    <tr key={de.id} className="hover:bg-gray-50 align-top">
                                        <td className="px-3 py-2 text-gray-800 font-medium">{de.name}</td>
                                        <td className="px-3 py-2 text-gray-700">{de.description}</td>
                                        <td className="px-3 py-2 text-gray-700">{de.businessValueName}</td>
                                        <td className="px-3 py-2 text-gray-700">
                                            {de.impactLabels && de.impactLabels.length > 0 && de.impactLabels[0] !== 'Non spécifié' ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {de.impactLabels.map((label, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-800 border border-blue-200"
                                                        >
                                                            {typeof label === 'string' ? label : (label?.impact || 'N/A')}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 text-sm font-medium text-center block">-</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <div className={`severity-box inline-flex flex-col items-center justify-center w-14 h-9 p-0.5 rounded text-[0.6rem] font-semibold border border-gray-200 ${de.severityInfo.bgClass} ${de.severityInfo.textClass}`}>
                                                <span>{de.severityInfo.level}</span>
                                                <span className="label text-[0.55rem] leading-tight mt-0.5">{de.severityInfo.label}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                )}
                {nonIntentionalRisks.length > 0 && (
                <div className="section-card bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
                    <h2 className="text-xl font-semibold text-amber-700 border-b border-gray-300 pb-2 mb-4 flex items-center">
                        <AlertTriangle size={20} className="mr-2 text-amber-600" />{t('riskTreatment.nonIntentionalRisks')}
                    </h2>
                    <div className="overflow-x-auto -mx-6 px-6">
                        <table className="min-w-full detail-table dreaded-event-table text-xs border border-gray-200">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-2 font-medium text-gray-600">{t('riskTreatment.table.headers.eventName')}</th>
                                    <th className="px-3 py-2 font-medium text-gray-600">{t('common.description')}</th>
                                    <th className="px-3 py-2 font-medium text-gray-600">{t('riskTreatment.table.headers.associatedBusinessValue')}</th>
                                    <th className="px-3 py-2 font-medium text-gray-600">{t('riskTreatment.table.headers.primaryImpact')}</th>
                                    <th className="px-3 py-2 font-medium text-gray-600 text-center">{t('riskTreatment.table.headers.initialSeverity')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {nonIntentionalRisks.map((de) => (
                                    <tr key={de.id} className="hover:bg-gray-50 align-top">
                                        <td className="px-3 py-2 text-gray-800 font-medium">{de.name}</td>
                                        <td className="px-3 py-2 text-gray-700">{de.description}</td>
                                        <td className="px-3 py-2 text-gray-700">{de.businessValueName}</td>
                                        <td className="px-3 py-2 text-gray-700">
                                            {de.impactLabels && de.impactLabels.length > 0 && de.impactLabels[0] !== 'Non spécifié' ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {de.impactLabels.map((label, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-800 border border-blue-200"
                                                        >
                                                            {typeof label === 'string' ? label : (label?.impact || 'N/A')}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 text-sm font-medium text-center block">-</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <div className={`severity-box inline-flex flex-col items-center justify-center w-14 h-9 p-0.5 rounded text-[0.6rem] font-semibold border border-gray-200 ${de.severityInfo.bgClass} ${de.severityInfo.textClass}`}>
                                                <span>{de.severityInfo.level}</span>
                                                <span className="label text-[0.55rem] leading-tight mt-0.5">{de.severityInfo.label}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                )}
                {detailedDreadedEvents.length === 0 && (
                    <div className="section-card bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
                        <h2 className="text-xl font-semibold text-red-700 border-b border-gray-300 pb-2 mb-4 flex items-center">
                            <AlertTriangle size={20} className="mr-2 text-red-600" />{t('riskTreatment.riskIdentification')}
                        </h2>
                        <p className="text-sm text-gray-500 italic text-center py-4">Aucun événement redouté défini.</p>
                    </div>
                )}
                    </>);
                })()}

                {/* Section: Detailed Treatment Plan */}
                <div className="section-card bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
                    <h2 className="text-xl font-semibold text-purple-700 border-b border-gray-300 pb-2 mb-4 flex items-center">
                        <CheckSquare size={20} className="mr-2 text-purple-600" />{t('riskTreatment.detailedPlan')}
                    </h2>
                    {detailedTreatmentPlan.length > 0 ? (
                        <div className="overflow-x-auto -mx-6 px-6"> {/* Allow horizontal scroll if needed */}
                            {/* Improved Table Style */}
                            <table className="min-w-full detail-table treatment-plan-table text-xs border border-gray-200">
                                <colgroup>
                                    <col style={{ width: '15%' }} />
                                    <col style={{ width: '12%' }} />
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: '8%' }} />
                                    <col style={{ width: '36%' }} />
                                    <col style={{ width: '9%' }} />
                                    <col style={{ width: '8%' }} />
                                </colgroup>
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-3 py-2 font-medium text-gray-600">{t('riskTreatment.table.headers.securityRule')}</th>
                                        <th className="px-3 py-2 font-medium text-gray-600">{t('riskTreatment.table.headers.gapComment')}</th>
                                        <th className="px-3 py-2 font-medium text-gray-600">{t('riskTreatment.table.headers.primaryImpact')}</th>
                                        <th className="px-3 py-2 font-medium text-gray-600 text-center">{t('riskTreatment.table.headers.initSeverity')}</th>
                                        <th className="px-3 py-2 font-medium text-gray-600">{t('riskTreatment.table.headers.associatedControls')} & {t('riskTreatment.table.headers.responsibleDecision')}</th>
                                        <th className="px-3 py-2 font-medium text-gray-600 text-center">{t('riskTreatment.table.headers.residSeverity')}</th>
                                        <th className="px-3 py-2 font-medium text-gray-600 text-center">{t('riskTreatment.table.headers.reduction')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {detailedTreatmentPlan.map((item, index) => {
                                        // Calculate reduction
                                        const initialValue = item.initialSeverity?.value || 0;
                                        const residualValue = item.residualSeverity?.value || 0;
                                        let reductionPercent = 'N/A';
                                        // Check if residual severity is defined (not 'N/A')
                                        const isResidualDefined = item.residualSeverity?.level !== 'N/A' && item.residualSeverity?.level !== 'Non défini';
                                        if (initialValue > 0 && isResidualDefined && residualValue < initialValue) {
                                            reductionPercent = Math.round(((initialValue - residualValue) / initialValue) * 100) + '%';
                                        } else if (initialValue > 0 && isResidualDefined && residualValue >= initialValue) {
                                            reductionPercent = '0%'; // No reduction or increase
                                        }

                                        // ---> Check if it's the start of a new rule group <---
                                        const isNewRuleGroup = index === 0 || (item.ruleId && detailedTreatmentPlan[index - 1]?.ruleId !== item.ruleId);

                                        // ---> Calculate rowSpan if it's a new group <---
                                        let ruleRowSpan = 1;
                                        if (isNewRuleGroup && item.ruleId) {
                                            for (let i = index + 1; i < detailedTreatmentPlan.length; i++) {
                                                if (detailedTreatmentPlan[i]?.ruleId === item.ruleId) {
                                                    ruleRowSpan++;
                                                } else {
                                                    break; // Stop counting when rule ID changes
                                                }
                                            }
                                        }

                                        return (
                                            <tr key={index} className="hover:bg-gray-50 align-top">
                                                {/* Conditionally render Rule cell with rowSpan */}
                                                {isNewRuleGroup && (
                                                    <td className="px-3 py-2 text-gray-800 font-medium align-top" rowSpan={ruleRowSpan}>{item.ruleName}</td>
                                                )}
                                                {isNewRuleGroup && (
                                                    <td className="px-3 py-2 text-gray-600 align-top text-[11px] whitespace-pre-wrap" rowSpan={ruleRowSpan}>
                                                        {item.gapComment ? item.gapComment : <span className="text-gray-300 italic">&mdash;</span>}
                                                    </td>
                                                )}
                                                <td className="px-3 py-2 text-gray-700">
                                                    {item.impactLabels && item.impactLabels.length > 0 && item.impactLabels[0] !== 'Non spécifié' ? (
                                                        <div className="flex flex-wrap gap-1">
                                                            {item.impactLabels.map((label, idx) => (
                                                                <span
                                                                    key={idx}
                                                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-800 border border-blue-200"
                                                                >
                                                                    {typeof label === 'string' ? label : (label?.impact || 'N/A')}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 text-sm font-medium text-center block">-</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    {/* Smaller Severity Box */}
                                                    <div className={`severity-box inline-flex flex-col items-center justify-center w-14 h-9 p-0.5 rounded text-[0.6rem] font-semibold border border-gray-200 ${item.initialSeverity.bgClass} ${item.initialSeverity.textClass}`}>
                                                        <span>{item.initialSeverity.level}</span>
                                                        <span className="label text-[0.55rem] leading-tight mt-0.5">{item.initialSeverity.label}</span>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 text-gray-700">
                                                    {item.controls.length > 0 ? (
                                                        <div className="space-y-3">
                                                            {item.controls.map((control, idx) => (
                                                                <div key={idx} className={`flex flex-col space-y-1.5 ${idx > 0 ? 'pt-3 border-t border-gray-100' : ''}`}>
                                                                    <div className="flex items-start gap-1.5 min-w-0">
                                                                        <span className="text-gray-400 mt-0.5 flex-shrink-0 text-[10px]">•</span>
                                                                        <span className="text-xs font-medium text-gray-700 break-words min-w-0" title={control.controlName}>
                                                                            {control.controlName}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pl-3.5">
                                                                        <div className="flex items-center gap-1.5">
                                                                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                                                                                {t('securityControls.sections.decision')}:
                                                                            </span>
                                                                            <span className="text-[11px] text-gray-600">
                                                                                {getDecisionLabel({ decision: control.decision, date: control.decisionDate })}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex items-center gap-1.5 border-l border-gray-100 pl-4">
                                                                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                                                                                {t('securityControls.sections.responsible')}:
                                                                            </span>
                                                                            <span className="text-[11px] text-gray-600">
                                                                                {control.responsiblePerson || '-'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    {control.decision === 'accepted' && control.acceptanceRationale && (
                                                                        <div className="text-[10px] italic text-gray-400 pl-3.5 mt-0.5 border-l-2 border-gray-100">
                                                                            Raison: {control.acceptanceRationale}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 text-sm font-medium text-center block">-</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    <div className={`severity-box inline-flex flex-col items-center justify-center w-14 h-9 p-0.5 rounded text-[0.6rem] font-semibold border border-gray-200 ${item.residualSeverity.bgClass} ${item.residualSeverity.textClass}`}>
                                                        <span>{item.residualSeverity.level}</span>
                                                        <span className="label text-[0.55rem] leading-tight mt-0.5">{item.residualSeverity.label}</span>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 text-center font-semibold text-gray-700">
                                                    {reductionPercent}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 italic text-center py-6">Aucun plan de traitement détaillé disponible (vérifiez l'étape 'Analyse et Association des Contrôles').</p>
                    )}
                </div>

                {/* --- NEW: Accepted Risks Summary --- */}
                {acceptedRisks.length > 0 && (
                    <div className="section-card bg-yellow-50 p-6 rounded-lg shadow-sm mb-6 border border-yellow-200">
                        <h2 className="text-xl font-semibold text-yellow-800 border-b border-yellow-300 pb-2 mb-4 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18" className="mr-2 text-yellow-600"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
                            {t('riskTreatment.acceptedRisks')}
                        </h2>
                        <p className="text-sm mb-4 text-yellow-900">Liste des risques pour lesquels la stratégie "Accepter" a été choisie :</p>
                        <div className="overflow-x-auto -mx-6 px-6">
                            <table className="min-w-full detail-table text-xs border border-yellow-300">
                                <thead className="bg-yellow-100">
                                    <tr>
                                        <th className="px-3 py-2 font-medium text-yellow-800 w-[30%]">Règle de Sécurité</th>
                                        <th className="px-3 py-2 font-medium text-yellow-800 w-[25%]">Impact Principal</th>
                                        <th className="px-3 py-2 font-medium text-yellow-800 w-[20%] text-center">Gravité Initiale</th>
                                        <th className="px-3 py-2 font-medium text-yellow-800 w-[25%] text-center">Gravité Résiduelle</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-yellow-200">
                                    {acceptedRisks.map((item) => (
                                        <tr key={item.id} className="hover:bg-yellow-50 align-top">
                                            <td className="px-3 py-2 text-gray-800 font-medium">{item.ruleName}</td>
                                            <td className="px-3 py-2 text-gray-700">
                                                {item.impactLabels && item.impactLabels.length > 0 && item.impactLabels[0] !== 'Non spécifié' ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {item.impactLabels.map((label, idx) => (
                                                            <span
                                                                key={idx}
                                                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-800 border border-yellow-200"
                                                            >
                                                                {typeof label === 'string' ? label : (label?.impact || 'N/A')}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400 text-sm font-medium text-center block">-</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                {/* Simplified severity display */}
                                                <span className={`font-semibold ${item.initialSeverity.textClass} ${item.initialSeverity.bgClass} px-1.5 py-0.5 rounded text-[0.65rem]`}>{item.initialSeverity.level}</span>
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <span className={`font-semibold ${item.residualSeverity.textClass} ${item.residualSeverity.bgClass} px-1.5 py-0.5 rounded text-[0.65rem]`}>{item.residualSeverity.level}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* --- NEW: Residual Risk Distribution --- */}
                <div className="section-card bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
                    <h2 className="text-xl font-semibold text-green-700 border-b border-gray-300 pb-2 mb-4 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18" className="mr-2 text-green-600"><path d="M15.5 2.75a.75.75 0 00-1.5 0v14.5a.75.75 0 001.5 0V2.75zM10.5 10a.75.75 0 00-1.5 0v7.25a.75.75 0 001.5 0V10zM5.5 5.75a.75.75 0 00-1.5 0v11.5a.75.75 0 001.5 0V5.75z" /></svg>
                        {t('riskTreatment.residualDistribution.title')}
                    </h2>
                    <p className="text-sm mb-4 text-gray-700">{t('riskTreatment.residualDistribution.description', { count: detailedTreatmentPlan.length })}</p>
                    <div className="overflow-x-auto -mx-6 px-6">
                        <table className="min-w-full detail-table text-xs border border-gray-200 w-3/4 mx-auto"> {/* Centered & Smaller Table */}
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-2 font-medium text-gray-600 w-1/2">{t('riskTreatment.table.headers.residualSeverityLevel')}</th>
                                    <th className="px-3 py-2 font-medium text-gray-600 w-1/2 text-center">{t('riskTreatment.table.headers.occurrences')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {Object.entries(residualRiskDistribution)
                                    // Show levels with count > 0, always show N/A if present
                                    .filter(([level, count]) => count > 0 || level === 'N/A')
                                    .map(([level, count]) => (
                                        <tr key={level} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 text-gray-700">
                                                {(() => {
                                                    // Find the label corresponding to the level code (G1, G2 etc.)
                                                    // Note: This assumes getSeverityLevelInfo returns an object with a 'level' property matching the key
                                                    // A better approach might be a dedicated map if levels are complex
                                                    const levelLabelMap = {
                                                        'G5': 'Catastrophique', 'G4': 'Critique', 'G3': 'Majeure',
                                                        'G2': 'Significative', 'G1': 'Mineure', 'N/A': 'Non défini'
                                                    };
                                                    const label = levelLabelMap[level] || level; // Fallback to level code if not found
                                                    return `${label} (${level})`;
                                                })()}
                                            </td>
                                            <td className="px-4 py-2 text-gray-700 text-center font-semibold">{count}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div> {/* End Print Area */}
        </motion.div>
    );
};

export default RiskTreatment;

